/**
 * @license
 * Copyright Google LLC All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */

/* eslint-disable */

import { Dialog, DialogConfig, DialogRef } from '@angular/cdk/dialog';
import {
  ComponentType,
  ConnectedPosition,
  FlexibleConnectedPositionStrategy,
  GlobalPositionStrategy,
  Overlay,
  OverlayContainer,
  PositionStrategy,
} from '@angular/cdk/overlay';
import { Location } from '@angular/common';
import {
  ANIMATION_MODULE_TYPE,
  ComponentRef,
  ElementRef,
  Inject,
  Injectable,
  InjectionToken,
  Injector,
  OnDestroy,
  Optional,
  SkipSelf,
  TemplateRef,
  Type,
  inject,
} from '@angular/core';
import {
  DialogPosition,
  MAT_DIALOG_DATA,
  MAT_DIALOG_DEFAULT_OPTIONS,
  MAT_DIALOG_SCROLL_STRATEGY,
  MatDialogConfig,
  MatDialogContainer,
  MatDialogRef,
} from '@angular/material/dialog';
import { Observable, Subject, defer } from 'rxjs';
import { startWith } from 'rxjs/operators';
import { ScrollLockService } from './scroll-lock.service';

// Counter for unique dialog ids.
let uniqueId = 0;

/**
 * Service to open Material Design modal dialogs.
 */
@Injectable({ providedIn: 'root' })
export class MatConnectedDialog implements OnDestroy {
  private readonly scrollLockService = inject(ScrollLockService);
  private readonly _openDialogsAtThisLevel: MatConnectedDialogRef<any>[] = [];
  private readonly _afterAllClosedAtThisLevel = new Subject<void>();
  private readonly _afterOpenedAtThisLevel = new Subject<MatConnectedDialogRef<any>>();

  protected _dialog: Dialog;
  protected dialogConfigClass = MatDialogConfig;

  private readonly _dialogRefConstructor: Type<MatConnectedDialogRef<any>>;
  private readonly _dialogContainerType: Type<MatDialogContainer>;
  private readonly _dialogDataToken: InjectionToken<any>;

  /** Keeps track of the currently-open dialogs. */
  get openDialogs(): MatConnectedDialogRef<any>[] {
    return this._parentDialog ? this._parentDialog.openDialogs : this._openDialogsAtThisLevel;
  }

  /** Stream that emits when a dialog has been opened. */
  get afterOpened(): Subject<MatConnectedDialogRef<any>> {
    return this._parentDialog ? this._parentDialog.afterOpened : this._afterOpenedAtThisLevel;
  }

  private _getAfterAllClosed(): Subject<void> {
    const parent = this._parentDialog;
    return parent ? parent._getAfterAllClosed() : this._afterAllClosedAtThisLevel;
  }

  /**
   * Stream that emits when all open dialog have finished closing.
   * Will emit on subscribe if there are no open dialogs to begin with.
   */
  readonly afterAllClosed: Observable<void> = defer(() =>
    this.openDialogs.length ? this._getAfterAllClosed() : this._getAfterAllClosed().pipe(startWith(undefined)),
  ) as Observable<any>;

  constructor(
    private _overlay: Overlay,
    injector: Injector,
    /**
     * @deprecated `_location` parameter to be removed.
     * @breaking-change 10.0.0
     */
    @Optional() location: Location,
    @Optional()
    @Inject(MAT_DIALOG_DEFAULT_OPTIONS)
    private _defaultOptions: MatConnectedDialogConfig,
    @Inject(MAT_DIALOG_SCROLL_STRATEGY) private _scrollStrategy: any,
    @Optional() @SkipSelf() private _parentDialog: MatConnectedDialog,
    /**
     * @deprecated No longer used. To be removed.
     * @breaking-change 15.0.0
     */
    _overlayContainer: OverlayContainer,
    /**
     * @deprecated No longer used. To be removed.
     * @breaking-change 14.0.0
     */
    @Optional()
    @Inject(ANIMATION_MODULE_TYPE)
    _animationMode?: 'NoopAnimations' | 'BrowserAnimations',
  ) {
    this._dialog = injector.get(Dialog);

    this._dialogRefConstructor = MatConnectedDialogRef;
    this._dialogContainerType = MatDialogContainer;
    this._dialogDataToken = MAT_DIALOG_DATA;
  }

  /**
   * Opens a modal dialog containing the given component.
   * @param component Type of the component to load into the dialog.
   * @param config Extra configuration options.
   * @returns Reference to the newly-opened dialog.
   */
  open<T, D = any, R = any>(
    component: ComponentType<T>,
    config?: MatConnectedDialogConfig<D>,
  ): MatConnectedDialogRef<T, R>;

  /**
   * Opens a modal dialog containing the given template.
   * @param template TemplateRef to instantiate as the dialog content.
   * @param config Extra configuration options.
   * @returns Reference to the newly-opened dialog.
   */
  open<T, D = any, R = any>(
    template: TemplateRef<T>,
    config?: MatConnectedDialogConfig<D>,
  ): MatConnectedDialogRef<T, R>;

  open<T, D = any, R = any>(
    template: ComponentType<T> | TemplateRef<T>,
    config?: MatConnectedDialogConfig<D>,
  ): MatConnectedDialogRef<T, R>;

  open<T, D = any, R = any>(
    componentOrTemplateRef: ComponentType<T> | TemplateRef<T>,
    config?: MatConnectedDialogConfig<D>,
  ): MatConnectedDialogRef<T, R> {
    let dialogRef: MatConnectedDialogRef<T, R>;
    this.scrollLockService.lock();
    config = {
      ...(this._defaultOptions || new MatConnectedDialogConfig()),
      ...config,
    };
    config.id = config.id || `mat-mdc-connected-dialog-${uniqueId++}`;
    config.scrollStrategy = config.scrollStrategy || this._scrollStrategy();

    config.panelClass = [
      ...(Array.isArray(config.panelClass) ? config.panelClass : config.panelClass ? [config.panelClass] : []),
      config.animation || '',
      config.mobileAnimation || '',
      config.mobileFixedBottom ? 'mat_dialog_mobile_fixed_bottom' : '',
    ].filter(Boolean);

    const positionStrategy = config.elementRef
      ? this._getConnectedPositionStrategy(config)
      : this._overlay.position().global().centerHorizontally().centerVertically();

    const cdkRef = this._dialog.open<R, D, T>(componentOrTemplateRef, {
      ...config,
      positionStrategy,
      // Disable closing since we need to sync it up to the animation ourselves.
      disableClose: true,
      // Disable closing on destroy, because this service cleans up its open dialogs as well.
      // We want to do the cleanup here, rather than the CDK service, because the CDK destroys
      // the dialogs immediately whereas we want it to wait for the animations to finish.
      closeOnDestroy: false,
      // Disable closing on detachments so that we can sync up the animation.
      // The Material dialog ref handles this manually.
      closeOnOverlayDetachments: false,
      container: {
        type: this._dialogContainerType,
        providers: () => [
          // Provide our config as the CDK config as well since it has the same interface as the
          // CDK one, but it contains the actual values passed in by the user for things like
          // `disableClose` which we disable for the CDK dialog since we handle it ourselves.
          { provide: this.dialogConfigClass, useValue: config },
          { provide: DialogConfig, useValue: config },
        ],
      },
      templateContext: () => ({ dialogRef }),
      providers: (ref, cdkConfig, dialogContainer) => {
        dialogRef = new this._dialogRefConstructor(ref, config, dialogContainer);
        dialogRef.updatePosition(config?.position);
        return [
          { provide: this._dialogContainerType, useValue: dialogContainer },
          { provide: this._dialogDataToken, useValue: cdkConfig.data },
          { provide: this._dialogRefConstructor, useValue: dialogRef },
          { provide: MatDialogRef, useValue: dialogRef },
        ];
      },
    });

    // This can't be assigned in the `providers` callback, because
    // the instance hasn't been assigned to the CDK ref yet.
    (dialogRef! as { componentRef: ComponentRef<T> }).componentRef = cdkRef.componentRef!;
    dialogRef!.componentInstance = cdkRef.componentInstance!;

    this.openDialogs.push(dialogRef!);
    this.afterOpened.next(dialogRef!);

    dialogRef!.afterClosed().subscribe(() => {
      this.scrollLockService.unlock();
      const index = this.openDialogs.indexOf(dialogRef);

      if (index > -1) {
        this.openDialogs.splice(index, 1);

        if (!this.openDialogs.length) {
          this._getAfterAllClosed().next();
        }
      }
    });

    return dialogRef!;
  }

  private _getConnectedPositionStrategy(dialogConfig: MatConnectedDialogConfig): FlexibleConnectedPositionStrategy {
    const positions = this._getPosition(dialogConfig);

    return this._overlay
      .position()
      .flexibleConnectedTo(dialogConfig.elementRef as ElementRef)
      .withLockedPosition()
      .withPositions(positions);
  }

  private _getPosition(config: MatConnectedDialogConfig): ConnectedPosition[] {
    if (config.positions) {
      return config.positions;
    } else {
      const offsetX = config.offsetX ?? 8;
      const offsetY = config.offsetY ?? 8;
      return [
        {
          offsetX: 0,
          offsetY: -offsetY,
          originX: 'center',
          overlayX: 'center',
          originY: 'top',
          overlayY: 'bottom',
        },
        {
          offsetX: 0,
          offsetY: offsetY,
          originX: 'center',
          overlayX: 'center',
          originY: 'bottom',
          overlayY: 'top',
        },
        {
          offsetX: offsetX,
          offsetY: 0,
          originX: 'end',
          overlayX: 'start',
          originY: 'center',
          overlayY: 'center',
        },
        {
          offsetX: -offsetX,
          offsetY: 0,
          originX: 'start',
          overlayX: 'end',
          originY: 'center',
          overlayY: 'center',
        },
        {
          offsetX: 0,
          offsetY: offsetY,
          originX: 'start',
          overlayX: 'start',
          originY: 'bottom',
          overlayY: 'top',
        },
        {
          offsetX: 0,
          offsetY: offsetY,
          originX: 'end',
          overlayX: 'end',
          originY: 'bottom',
          overlayY: 'top',
        },
        {
          offsetX: 0,
          offsetY: -offsetY,
          originX: 'start',
          overlayX: 'start',
          originY: 'top',
          overlayY: 'bottom',
        },
        {
          offsetX: 0,
          offsetY: -offsetY,
          originX: 'end',
          overlayX: 'end',
          originY: 'top',
          overlayY: 'bottom',
        },
      ];
    }
  }

  /**
   * Closes all of the currently-open dialogs.
   */
  closeAll(): void {
    this._closeDialogs(this.openDialogs);
  }

  /**
   * Finds an open dialog by its id.
   * @param id ID to use when looking up the dialog.
   */
  getDialogById(id: string): MatConnectedDialogRef<any> | undefined {
    return this.openDialogs.find(dialog => dialog.id === id);
  }

  ngOnDestroy() {
    // Only close the dialogs at this level on destroy
    // since the parent service may still be active.
    this._closeDialogs(this._openDialogsAtThisLevel);
    this._afterAllClosedAtThisLevel.complete();
    this._afterOpenedAtThisLevel.complete();
  }

  private _closeDialogs(dialogs: MatConnectedDialogRef<any>[]) {
    let i = dialogs.length;

    while (i--) {
      dialogs[i].close();
    }
  }
}

export enum MatConnectedDialogAnimation {
  SLIDE_UP = 'mat_dialog_slideUp',
  SLIDE_DOWN = 'mat_dialog_slideDown',
  SLIDE_LEFT = 'mat_dialog_slideLeft',
  SLIDE_RIGHT = 'mat_dialog_slideRight',
  FLEXIBLE_SLIDE_DOWN = 'mat_flexibleDialog_slideDown',
}

export enum MatConnectedDialogMobileAnimation {
  SLIDE_UP = 'mat_dialog_slideUp_mobile',
  SLIDE_DOWN = 'mat_dialog_slideDown_mobile',
  SLIDE_LEFT = 'mat_dialog_slideLeft_mobile',
  SLIDE_RIGHT = 'mat_dialog_slideRight_mobile',
}

export class MatConnectedDialogConfig<D = any> extends MatDialogConfig<D> {
  public elementRef?: ElementRef;

  public overlay?: boolean = false;

  public positions?: ConnectedPosition[];

  public offsetX?: number = 8;
  public offsetY?: number = 8;

  public animation?: MatConnectedDialogAnimation;

  public mobileAnimation?: MatConnectedDialogMobileAnimation;

  public mobileFixedBottom?: boolean = false;
}

export class MatConnectedDialogRef<T, R = any> extends MatDialogRef<T, R> {
  public constructor(
    private readonly __ref: DialogRef<R, T>,
    config: MatDialogConfig,
    _containerInstance: MatDialogContainer,
  ) {
    super(__ref, config, _containerInstance);
  }

  /**
   * Updates the dialog's position.
   * @param position New dialog position.
   */
  override updatePosition(position?: DialogPosition): this {
    const strategy = this.__ref.config.positionStrategy as PositionStrategy;

    if (strategy instanceof GlobalPositionStrategy) {
      if (position && (position.left || position.right)) {
        position.left ? strategy.left(position.left) : strategy.right(position.right);
      } else {
        strategy.centerHorizontally();
      }

      if (position && (position.top || position.bottom)) {
        position.top ? strategy.top(position.top) : strategy.bottom(position.bottom);
      } else {
        strategy.centerVertically();
      }
    }

    return this;
  }
}
